// Adapter: GT launcher result → ctsReview shape.
//
// gt-api returns three launcher result shapes today, depending on how the
// dispatch entered:
//
//   * PDF dispatch (launch_campaign_from_pdf): full io_json (parsed IO with
//     per-field validation) + conversion_metadata (LLM judge verdict).
//   * Excel dispatch (launch_campaign_from_config_file): input_summary
//     (counts from GTExcelParser) only — the full parsed config isn't
//     surfaced on the launch result.
//   * ZIP dispatch (launch_campaign_from_zip): extracts the inner Excel
//     and chains into launch_campaign_from_config_file, so the shape is
//     identical to the Excel dispatch, plus uploaded_excel_file_id/url
//     pointing at the extracted Excel in Drive.
//
// All three carry the same campaign_id, details (adgroup_ids,
// std_assignment_result, combined_parsed_responses, pause, creative_ids),
// errors[], warnings[], url, and success flag.
//
// Rather than fork the review pane, we map every shape onto the
// ctsReview.result.adgroups[] grid that the existing CLW renders. The
// post-launch view is read-only (alreadyLaunched=true is set by the
// caller); the pane's tiles + grid + breakdown + issues bar all light up
// from the synthetic ctsReview we return here.
//
// Lossy fields:
//   - PDF: per-adgroup budget split from io_json.packages by parsing the
//     `PKG N_…` prefix off each adgroup name and dividing the package
//     budget evenly across its adgroups.
//   - Excel/ZIP: no per-package budget data available on the launch
//     result, so budget / bid_price / ad_size on each row fall back to
//     null. The grid renders '—' for blank cells.

// Detect any gt-api launcher result on a planning-review payload. We look
// for the consistent gt-api `details` block (adgroup_ids + at least one
// assignment array) so the detector works for PDF, Excel, and ZIP
// dispatches alike. DV360 / TTD results have insertionOrders / Campaigns
// instead, so they don't trigger this path.
export function isGtLauncherResult(lr) {
  if (!lr || typeof lr !== 'object') return false;
  const d = lr.details;
  if (!d || typeof d !== 'object') return false;
  return Array.isArray(d.adgroup_ids)
    || Boolean(d.std_assignment_result)
    || Boolean(d.combined_parsed_responses);
}

// Recursively walk io_json.validation extracting every leaf
// {value, status, notes} entry tagged with its dotted path.
function flattenValidation(node, path = '') {
  const out = [];
  if (!node || typeof node !== 'object') return out;
  if ('value' in node && 'status' in node) {
    out.push({ path, value: node.value, status: node.status, notes: node.notes });
    return out;
  }
  if (Array.isArray(node)) {
    node.forEach((child, i) => out.push(...flattenValidation(child, `${path}[${i}]`)));
    return out;
  }
  for (const [k, v] of Object.entries(node)) {
    if (k === 'summary') continue;
    out.push(...flattenValidation(v, path ? `${path}.${k}` : k));
  }
  return out;
}

// Parse "PKG 3_AV Mob_Mobile_STD_DMA_..." → { packageNumber: '3',
// productHint: 'AV Mob', deviceTargeting: 'Mobile', creativeType: 'STD',
// locationTargeting: 'DMA' }. Anything we can't parse falls back to
// empty strings, which is fine — the grid renders '—' for blank cells.
function parseAdgroupName(name) {
  if (!name || typeof name !== 'string') return {};
  const parts = name.split('_');
  const pkgMatch = parts[0] && parts[0].match(/PKG\s*(\d+)/i);
  return {
    packageNumber: pkgMatch ? pkgMatch[1] : '',
    productHint: parts[1] || '',
    deviceTargeting: parts[2] || '',
    creativeType: parts[3] || '',
    locationTargeting: parts[4] || '',
    targetingProduct: parts[5] || '',
  };
}

// Heuristic bucket so the warnings bar can group repetitive launcher
// warnings (audio adgroup not supported × N, segment-not-found × N, …)
// into one issue line instead of N — the CTS review pane gets very busy
// otherwise.
function warningCategory(w) {
  if (/AUDIO_ADGROUP_NOT_SUPPORTED/.test(w)) return 'Audio ad groups not yet supported by GT';
  if (/MISSING_PRODUCT_TARGETING_EXCEPTION/.test(w)) return 'On-premise locations not selected';
  if (/Location group not found/.test(w)) return 'Location group not found';
  if (/Segment not found/.test(w)) return 'Retargeting segment pending';
  // Stale picks must surface, not vanish: the V2 sanitizer reports every
  // audience it strips, with its type and id (R18).
  if (/Removed (stale|invalid|offending) audience/i.test(w)) return 'Stale audience removed';
  return 'Launch warning';
}

export function gtLauncherToCtsReview(lr) {
  if (!isGtLauncherResult(lr)) return null;
  const io = lr.io_json || null;             // PDF dispatch only
  const conv = lr.conversion_metadata || null; // PDF dispatch only
  const inputSummary = lr.input_summary || null; // Excel / ZIP dispatch only
  const details = lr.details || {};
  const pkgs = (io && Array.isArray(io.packages)) ? io.packages : [];
  const flow = io ? 'pdf' : (lr.uploaded_excel_file_id ? 'zip' : 'excel');

  // ── adgroups ───────────────────────────────────────────────
  // Use std_assignment_result.assignments as the authoritative list of
  // launched adgroups (one entry per ad group that reached the GT API).
  // Fall back to combined_parsed_responses for adgroups that didn't get
  // a std-assignment entry (e.g. audio ad groups that failed to launch).
  const stdAssign = details.std_assignment_result?.assignments || [];
  const custAssign = details.combined_parsed_responses?.custom_audience_adgroups?.assignments || [];
  const tpAssign = details.combined_parsed_responses?.thirdparty_audience_adgroups?.assignments || [];
  const audByName = new Map();
  for (const a of [...custAssign, ...tpAssign]) {
    if (!a.adgroup_name) continue;
    const row = audByName.get(a.adgroup_name) || { custom: [], thirdParty: [], reasoning: null };
    if (a.custom_audiences) row.custom.push(...a.custom_audiences);
    if (a.third_party_audiences) row.thirdParty.push(...a.third_party_audiences);
    if (!row.reasoning && a.reasoning) row.reasoning = a.reasoning;
    audByName.set(a.adgroup_name, row);
  }

  // Pre-count adgroups per package so we can split package budget evenly
  // (only meaningful for PDF flow where io_json.packages carries budgets).
  const adgroupNames = new Set();
  for (const a of stdAssign) if (a.adgroup_name) adgroupNames.add(a.adgroup_name);
  for (const n of audByName.keys()) adgroupNames.add(n);
  const pkgAdgroupCounts = new Map();
  for (const name of adgroupNames) {
    const { packageNumber } = parseAdgroupName(name);
    if (!packageNumber) continue;
    pkgAdgroupCounts.set(packageNumber, (pkgAdgroupCounts.get(packageNumber) || 0) + 1);
  }

  const buildAdgroup = (name, id) => {
    const parsed = parseAdgroupName(name);
    const pkgIdx = parsed.packageNumber ? Number(parsed.packageNumber) - 1 : -1;
    const pkg = pkgIdx >= 0 && pkgIdx < pkgs.length ? pkgs[pkgIdx] : null;
    const aud = audByName.get(name) || { custom: [], thirdParty: [], reasoning: null };
    const audienceNames = [
      ...aud.custom.map((a) => a.name || a).filter(Boolean),
      ...aud.thirdParty.map((a) => a.name || a).filter(Boolean),
    ];
    // PDF flow: per-package budget split from io_json. Excel / ZIP flow:
    // no per-package budget on the launch result, so leave null (renders
    // as '—' in the grid).
    const splitBudget = pkg && pkg.budget != null && pkgAdgroupCounts.get(parsed.packageNumber)
      ? Number(pkg.budget) / pkgAdgroupCounts.get(parsed.packageNumber)
      : null;
    return {
      // ─ Identity ─
      ad_group_name: name,
      adgroup_id: id || null,
      // ─ Flight ─
      start_date: pkg?.start_date || io?.campaign?.start_date || null,
      end_date: pkg?.end_date || io?.campaign?.end_date || null,
      // ─ Package / product (parsed from the adgroup name in all flows) ─
      package_number: parsed.packageNumber || null,
      product: parsed.productHint || null,
      targeting_product: parsed.targetingProduct || null,
      device_targeting: parsed.deviceTargeting || null,
      device_types: parsed.deviceTargeting || null,
      location_targeting: parsed.locationTargeting || null,
      creative_type: parsed.creativeType || null,
      // ─ Pricing (PDF flow only — Excel / ZIP launch result has no rates) ─
      bid_price: pkg?.cpm != null && typeof pkg.cpm === 'number' ? pkg.cpm : null,
      bid_type: pkg?.rate_type || null,
      budget: splitBudget,
      // ─ Creatives (PDF flow only) ─
      creative_sizes: pkg?.ad_size || null,
      // ─ Notes ─
      special_notes: audienceNames.length ? audienceNames.join(' · ') : (aud.reasoning || null),
    };
  };

  const adgroups = [];
  const seen = new Set();
  for (const a of stdAssign) {
    if (!a.adgroup_name || seen.has(a.adgroup_name)) continue;
    seen.add(a.adgroup_name);
    adgroups.push(buildAdgroup(a.adgroup_name, a.adgroup_id));
  }
  for (const name of audByName.keys()) {
    if (seen.has(name)) continue;
    seen.add(name);
    adgroups.push(buildAdgroup(name, null));
  }

  // ── issues from validation (PDF only; per-field ✗ on io_json.validation) ──
  // The IO validation is per-field on the parsed PDF — not per-adgroup.
  // We surface them as campaign-level warnings (existing pane shows
  // ctsReview.warnings in the issues bar). Excel / ZIP dispatches don't
  // run this validation step.
  const validation = io ? flattenValidation(io.validation || {}) : [];
  const fieldIssues = validation.filter((v) => v.status !== '✓');
  const fieldIssueMessages = fieldIssues.map((v) => `${v.path}: ${v.value ?? ''}${v.notes ? ` — ${v.notes}` : ''}`);

  // ── launch warnings (collapse repeats) ─────────────────────
  const launchWarnings = Array.isArray(lr.warnings) ? lr.warnings : [];
  const warningBuckets = new Map();
  for (const w of launchWarnings) {
    const cat = warningCategory(w);
    const arr = warningBuckets.get(cat) || [];
    arr.push(w);
    warningBuckets.set(cat, arr);
  }
  const collapsedWarnings = [];
  for (const [cat, items] of warningBuckets.entries()) {
    if (items.length === 1) collapsedWarnings.push(items[0]);
    else collapsedWarnings.push(`${cat} (×${items.length}): ${items[0]}${items.length > 1 ? ` …+${items.length - 1} more` : ''}`);
  }

  // ── infos: conversion metadata + judge verdict (PDF) or Excel link (ZIP) ──
  const infos = [];
  if (conv?.selected_model) {
    const dur = conv.duration_seconds ? ` in ${Math.round(conv.duration_seconds)}s` : '';
    infos.push(`PDF → IO via ${conv.selected_model}${dur} (${conv.method || 'multi_model'})`);
  }
  if (conv?.judge_validation?.recommendation) {
    const verdict = conv.judge_validation.validation_passed === false ? 'rejected' : 'accepted';
    infos.push(`Judge ${verdict}: ${conv.judge_validation.recommendation}${conv.judge_validation.reasoning ? ` — ${conv.judge_validation.reasoning}` : ''}`);
  }
  if (flow === 'zip' && lr.uploaded_excel_file_url) {
    infos.push(`Extracted Excel: ${lr.uploaded_excel_file_url}`);
  }

  // ── deterministic post-processing (conversion_metadata.postprocess) ──
  // The converter re-allocates budgets and checks package/ad-group structure
  // after the model returns. Those findings were reaching the GCS payload but
  // were never rendered, so a reviewer had no way to see them. The per-package
  // summary matters most: code cannot prove a package lost a line item without
  // line-item ground truth, but "PKG 1: 1 ad group, $15,000" is obviously wrong
  // to a human reviewing a 4-line-item package.
  const post = conv?.postprocess;
  if (post) {
    for (const line of post.summary || []) infos.push(line);

    const postWarnings = [
      ...(post.adgroup_count?.warnings || []),
      ...(post.budget?.warnings || []),
      ...(post.naming?.warnings || []),
      ...(post.placeholders?.warnings || []),
    ];
    for (const w of postWarnings) collapsedWarnings.push(w);

    for (const a of post.advisories || []) infos.push(`Advisory: ${a}`);

    const adjusted = [
      ...(post.budget?.adjusted || []),
      ...(post.naming?.adjusted || []),
      ...(post.placeholders?.adjusted || []),
    ];
    if (adjusted.length) {
      infos.push(`Auto-corrected ${adjusted.length} field${adjusted.length === 1 ? '' : 's'} after conversion (budget allocation, naming, placeholders)`);
    }
  }
  if (inputSummary) {
    const parts = [];
    if (inputSummary.adgroups_items_found != null) parts.push(`${inputSummary.adgroups_items_found} adgroups parsed`);
    if (inputSummary.creatives_new_items_found != null) parts.push(`${inputSummary.creatives_new_items_found} creatives`);
    if (inputSummary.custom_audiences_items_found != null) parts.push(`${inputSummary.custom_audiences_items_found} custom audiences`);
    if (inputSummary.dcm_tags_items_found != null) parts.push(`${inputSummary.dcm_tags_items_found} DCM tags`);
    if (parts.length) infos.push(`Excel parsed: ${parts.join(', ')}`);
  }

  // ── totals ─────────────────────────────────────────────────
  // PDF: sum from io_json.packages. Excel / ZIP: no budget data on the
  // launch result, so io_budget stays null (the tile renders '—').
  const totalBudget = pkgs.reduce((acc, p) => acc + (typeof p.budget === 'number' ? p.budget : 0), 0);

  // ── name / summary (PDF io_json or Excel input_summary fallback) ──
  const campaignName = io?.campaign?.campaign_name || null;
  const startDate = io?.campaign?.start_date || null;
  const endDate = io?.campaign?.end_date || null;
  const accountExec = io?.campaign?.groundtruth_team?.sales_rep || null;
  const accountManager = io?.campaign?.groundtruth_team?.account_manager || null;

  return {
    request_id: `gt-${flow}-${lr.campaign_id || 'unknown'}`,
    result: {
      campaign: {
        io_name: campaignName,
        salesForce_opportunity_name: campaignName,
        start_date: startDate,
        end_date: endDate,
        account_executive: accountExec,
        account_manager: accountManager,
        ad_ops_manager: null,
        io_budget: totalBudget > 0 ? totalBudget : null,
        kpi_1: null,
        kpi_2: null,
      },
      adgroups,
      summary: {
        adgroups_items_found: inputSummary?.adgroups_items_found ?? adgroups.length,
        creatives_new_items_found: inputSummary?.creatives_new_items_found ?? (details.creative_ids || []).length,
        custom_audiences_items_found: inputSummary?.custom_audiences_items_found
          ?? (custAssign || []).reduce((a, x) => a + (x.custom_audiences?.length || 0), 0),
        third_party_audiences_items_found: inputSummary?.third_party_audiences_items_found
          ?? (tpAssign || []).reduce((a, x) => a + (x.third_party_audiences?.length || 0), 0),
        location_lists_items_found: inputSummary?.location_lists_items_found ?? 0,
        dcm_tags_items_found: inputSummary?.dcm_tags_items_found ?? 0,
      },
      // Carry the launched campaign id + URL through so the review header
      // can link out to GroundTruth.
      gt_campaign_id: lr.campaign_id || null,
      gt_campaign_url: lr.url || null,
      gt_excel_drive_url: lr.uploaded_excel_file_url || null, // ZIP only
    },
    // Severity buckets used by the issues bar. PDF validation mismatches
    // and launch warnings live in `warnings`; hard launch failures live
    // in `errors`; conversion metadata + extracted-Excel link live in
    // `infos`.
    errors: Array.isArray(lr.errors) ? lr.errors : [],
    warnings: [...fieldIssueMessages, ...collapsedWarnings],
    infos,
    // Per-adgroup issues would let row annotations light up, but the PDF
    // validation is per-IO-field (not per-adgroup) and Excel / ZIP
    // launches don't surface any. Leave empty — the issues bar still
    // surfaces everything via .warnings.
    issues: [],
  };
}

